import { useState } from 'react'
import { Ban, ChevronDown, ChevronUp, Flame, Loader2, Trash2, TriangleAlert } from 'lucide-react'
import type { Category, Order } from '@/types'
import { isBillableLine, isUnsentLine } from './lineVisibility'

/**
 * F-CTRL-N41: 'served' is voidable. It does not mean the guest accepted the
 * item — a per-station bump stamps a line 'served' the moment the barista marks
 * it done, and marking an order served bulk-stamps every active line. Excluding
 * it here meant a drink that was made, carried out and refused at the table
 * could not be voided, only discounted to zero, which needs no manager PIN and
 * writes no void_log row.
 *
 * Mirrors voidableItemStatus() in backend/internal/handlers/kot.go — the
 * backend refuses anything this list wrongly offers, so the two must agree.
 */
export const VOIDABLE_STATUSES = new Set(['sent', 'preparing', 'ready', 'served'])

export interface SentItemsSectionProps {
  order: Order | null
  categoryById: Map<string, Category>
  /** Category accent color function (usually inherited from the parent). */
  categoryColor: (cat: Category | undefined, fallback: string) => string
  formatCurrency: (n: number) => string
  /** Default expanded state for the section. */
  defaultExpanded?: boolean
  /** When provided, a void button is shown on each voidable row. */
  onRequestVoid?: (itemId: string, itemName: string, qty: number, unitPrice: number) => void
  /**
   * When provided, a "Void order" button sits in the header and voids every
   * voidable line under one reason + manager PIN. Shown only while at least
   * one line is voidable and the invoice has not been filed (a filed order is
   * reversed by credit note, and VoidItem refuses it anyway).
   */
  onRequestVoidOrder?: () => void
  /**
   * F-FE-N154. When provided, a remove button is shown on each UNSENT row.
   * Deliberately not the void flow: void exists to audit food that was made and
   * destroyed, and an unsent line was never made, so there is nothing for a
   * manager PIN to authorise and nothing for void_log to record. The backend
   * agrees — RemoveDraftItem takes no PIN and accepts only draft/pending.
   */
  onRequestRemove?: (itemId: string, itemName: string) => void
  /** Row currently being removed, so its button can show progress. */
  removingItemId?: string | null
  /**
   * F-FE-N154. When provided, offers a one-tap fire for lines that never
   * reached the kitchen. Without it the only way to send them is to add an
   * unrelated item and fire the whole order, which prints the stale lines too.
   */
  onFireUnsent?: () => void
  fireUnsentPending?: boolean
}

export function SentItemsSection({
  order,
  categoryById,
  categoryColor,
  formatCurrency,
  defaultExpanded = true,
  onRequestVoid,
  onRequestVoidOrder,
  onRequestRemove,
  removingItemId,
  onFireUnsent,
  fireUnsentPending,
}: SentItemsSectionProps) {
  // F-FE-N154: the SAME predicate the checkout panel bills through. These were
  // two hand-written filters that disagreed about 'draft', so an order whose
  // KOT fire never landed showed a payable total and no lines at all — see
  // audit/PARCHED_UNFIRED_ORDER_NO_VOID_BANNER_2026-09-10.md.
  const items = (order?.items ?? []).filter(isBillableLine)
  const unsent = items.filter(isUnsentLine)
  const [expanded, setExpanded] = useState(defaultExpanded)

  if (!order || items.length === 0) return null

  const total = items.reduce((s, i) => s + i.total_price, 0)
  const voidableCount = onRequestVoid
    ? items.filter((i) => !isUnsentLine(i) && VOIDABLE_STATUSES.has(i.status)).length
    : 0
  const filed = String(order.fiscal_details?.status ?? '').toUpperCase() === 'SYNCED'
  const showVoidOrder = !!onRequestVoidOrder && voidableCount > 0 && !filed

  return (
    <section aria-labelledby="sent-items-heading" className="rounded-lg border border-border bg-muted/15">
      {/*
        The header is two controls side by side: the title/total toggles the
        list, and "Void order" is its own button — a button cannot sit inside
        another one.
      */}
      <div className="flex items-center gap-2 rounded-t-lg pr-2">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="flex min-w-0 flex-1 items-center justify-between gap-3 rounded-tl-lg px-3 py-2.5 text-left hover:bg-muted/30 transition-colors"
      >
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            On order
          </div>
          <div id="sent-items-heading" className="flex items-center gap-2 text-sm font-medium">
            <Flame className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-300" aria-hidden />
            Order #{order.order_number}
            {order.offline_ticket_ref ? (
              <span className="ml-1 font-mono text-[10px] text-muted-foreground">
                {order.offline_ticket_ref}
              </span>
            ) : null}{' '}
            · {items.length}
            <span className="text-muted-foreground font-normal">items</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold tabular-nums">{formatCurrency(total)}</span>
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" aria-hidden />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
          )}
        </div>
      </button>
      {showVoidOrder && (
        <button
          type="button"
          onClick={onRequestVoidOrder}
          title="Void every item on this order — needs a manager PIN"
          className="inline-flex h-8 shrink-0 items-center gap-1 rounded-md border border-destructive/40 px-2 text-xs font-semibold text-destructive transition-colors hover:bg-destructive/10"
        >
          <Ban className="h-3.5 w-3.5" aria-hidden />
          Void order
        </button>
      )}
      </div>

      {/*
        F-FE-N154. Unsent lines are still on the bill, so this says so plainly
        rather than blocking anything: the till must never refuse a sale because
        our record of the fire didn't land — the kitchen may well have the food.
        The last clause is the fire-sweep warning: kot.go fires every draft line
        on the order, so adding one item to a stale check reprints all of them.
      */}
      {unsent.length > 0 && (
        <div className="mx-2 mb-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-[11px] leading-snug text-amber-900 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-100">
          <div className="flex items-start gap-1.5">
            <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            <div className="min-w-0">
              <span className="font-semibold">
                {unsent.length} item{unsent.length === 1 ? '' : 's'} never reached the kitchen.
              </span>{' '}
              Still on the bill. Send or remove {unsent.length === 1 ? 'it' : 'them'} — otherwise
              {unsent.length === 1 ? ' it fires' : ' they fire'} with your next Send.
              {onFireUnsent && (
                <div className="mt-1.5">
                  <button
                    type="button"
                    onClick={onFireUnsent}
                    disabled={fireUnsentPending}
                    className="inline-flex items-center gap-1.5 rounded border border-amber-400 bg-amber-100 px-2 py-1 text-[11px] font-semibold text-amber-900 transition-colors hover:bg-amber-200 disabled:opacity-60 dark:border-amber-700 dark:bg-amber-800/50 dark:text-amber-50 dark:hover:bg-amber-800"
                  >
                    {fireUnsentPending ? (
                      <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                    ) : (
                      <Flame className="h-3 w-3" aria-hidden />
                    )}
                    {fireUnsentPending ? 'Sending…' : 'Send to kitchen'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {expanded && (
        <div className="overflow-x-auto px-2 pb-3">
          <table className="w-full min-w-[300px] table-fixed border-collapse border border-border text-sm">
            <colgroup>
              <col style={{ width: '40%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '24%' }} />
              <col style={{ width: '26%' }} />
            </colgroup>
            <thead>
              <tr className="border-b border-border bg-muted/60">
                <th className="px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Item
                </th>
                <th className="px-2 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Qty
                </th>
                <th className="px-2 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Amount
                </th>
                <th className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {onRequestVoid ? 'Void' : onRequestRemove ? '' : 'Status'}
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const exCat = item.product?.category_id ? categoryById.get(item.product.category_id) : undefined
                const exAccent = categoryColor(exCat, item.product?.name ?? 'Item')
                const lineDiscount = item.discount_amount ?? 0
                const itemUnsent = isUnsentLine(item)
                const removing = removingItemId === item.id
                return (
                  <tr
                    key={item.id}
                    className="border-b border-border last:border-b-0 border-l-[3px]"
                    style={{
                      borderLeftColor: exAccent,
                      backgroundColor: `color-mix(in srgb, ${exAccent} 10%, hsl(var(--card)))`,
                    }}
                  >
                    <td className="min-w-0 px-2 py-2">
                      <span className="line-clamp-2 font-medium leading-snug">
                        {item.product?.name ?? 'Item'}
                      </span>
                      {itemUnsent && (
                        <div className="mt-0.5">
                          <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-amber-900 dark:border-amber-800 dark:bg-amber-900/40 dark:text-amber-100">
                            Not sent
                          </span>
                        </div>
                      )}
                      {lineDiscount > 0 && (
                        <div className="mt-0.5 text-[11px] tabular-nums text-amber-700 dark:text-amber-300">
                          Discount −{formatCurrency(lineDiscount)}
                          {item.discount_percent != null ? ` (${item.discount_percent}%)` : ''}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right text-sm tabular-nums">{item.quantity}</td>
                    <td className="px-2 py-2 text-right text-sm font-medium tabular-nums">
                      {formatCurrency(item.total_price)}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {itemUnsent && onRequestRemove ? (
                        <button
                          type="button"
                          title="Remove item — never sent to the kitchen"
                          disabled={removing}
                          onClick={(e) => {
                            e.stopPropagation()
                            onRequestRemove(item.id, item.product?.name ?? 'Item')
                          }}
                          className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                        >
                          {removing ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5" aria-hidden />
                          )}
                          <span className="sr-only">Remove {item.product?.name ?? 'item'}</span>
                        </button>
                      ) : onRequestVoid && VOIDABLE_STATUSES.has(item.status) ? (
                        <button
                          type="button"
                          title="Void item"
                          onClick={(e) => {
                            e.stopPropagation()
                            onRequestVoid(item.id, item.product?.name ?? 'Item', item.quantity, item.unit_price)
                          }}
                          aria-label={`Void ${item.product?.name ?? 'item'}`}
                          className="inline-flex h-8 items-center justify-center gap-1 rounded-md border border-destructive/40 bg-card px-2 text-xs font-semibold text-destructive transition-colors hover:bg-destructive/10"
                        >
                          <Ban className="h-3.5 w-3.5" aria-hidden />
                          Void
                        </button>
                      ) : (
                        <span className="text-[11px] text-muted-foreground capitalize">{item.status}</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {voidableCount > 0 && (
            <p className="mt-2 px-0.5 text-[11px] leading-snug text-muted-foreground">
              Sent to the kitchen. Tap Void to take an item off the bill — a manager PIN is needed.
            </p>
          )}
        </div>
      )}
    </section>
  )
}
